import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faUsers } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, forkJoin, of } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { HttpError } from 'src/app/core/http-error';
import { BoxAccessibility } from 'src/app/models/box-accessibility';
import { Rights } from 'src/app/models/enums/rights';
import { Group } from 'src/app/models/group';
import { IGroup } from 'src/app/models/interfaces/group.interface';
import { IParamsValues } from 'src/app/models/interfaces/params-values.interface';
import { ParamsValues } from 'src/app/models/params-values';
import { ViewAccessibility } from 'src/app/models/view-accessibility';
import { GroupsService } from 'src/app/services/groups.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { ParamsValuesService } from 'src/app/services/params-values.service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { GroupDetailContext } from './group-detail-context';
import { GroupDetailComponent } from './group-detail/group-detail.component';
import { GroupsListComponent } from './groups-list/groups-list.component';
import { GroupsTreeComponent } from './groups-tree/groups-tree.component';

enum DisplayMode {
    List = 1,
    Tree = 2,
    Form = 4,
}

@Component({
    selector: 'app-groups',
    templateUrl: './groups.component.html',
    styleUrls: ['./groups.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, GroupsListComponent, GroupsTreeComponent, DetailHeaderComponent, GroupDetailComponent],
})
export class GroupsComponent implements OnInit {
    /**
     * Reference au composant GroupsList.
     */
    @ViewChild(GroupsListComponent) groupsList: GroupsListComponent;

    /**
     * Reference au composant GroupsTree.
     */
    @ViewChild(GroupsTreeComponent) groupsTree: GroupsTreeComponent;

    /**
     * Liste des icônes utilisés dans le template
     */
    public faGroup = faUsers;

    /**
     * Liste des enums utilisés dans le template
     */
    public FormMode = FormMode;
    public DisplayMode = DisplayMode;

    /**
     * Contexte du formulaire.
     */
    public context: GroupDetailContext = null;

    /**
     * Mode d'affichage de la liste (Liste, Arborescence, formulaire)
     */
    public displayMode = DisplayMode.List;

    /**
     * Indique si le bouton d'édition doit apparaitre.
     */
    public displayEditbuttom = false;

    /**
     * Constructeur.
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param groupsService : Service métier utilisé pour gérer les groupes.
     * @param paramsValuesService : Service métier utilisé pour gérer les paramètres.
     * @param memoryService : Service utilisé pour gérer le contexte courant.
     */
    constructor(
        protected notificationService: NotificationService,
        private groupsService: GroupsService,
        private paramsValuesService: ParamsValuesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {}

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        // Active le bouton d'édition si l'utilisateur possède les droits.
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.GroupAndUserEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un groupe.
     * @param group Groupe selectionné.
     */
    public onGroupSelected(group: Group) {
        this.reloadForm(group, FormMode.Reading);
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param group Group sélectionné.
     */
    public onGroupDisplayed(group: Group) {
        this.reloadForm(group, FormMode.Reading);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param group Group sélectionné.
     */
    public onGroupEdited(group: Group) {
        this.reloadForm(group, FormMode.Edition);
    }

    /**
     * Appelé lorsque les données du groupe ont été mise à jour.
     * Il s'agit des modifications apportées à l'onglet Infos.
     * @param group : Données du groupe à modifier.
     */
    public groupUpdated(group: Group): void {
        this.spinner.show('main-spinner');
        if (this.context.editedGroupId) {
            // Cas de la mise à jour.
            this.groupsService.update(this.context.editedGroupId, group).subscribe(
                (x) => {
                    this.refreshListOrTree(x.id);
                    this.displaySuccessNotification('Groupe mis à jour');
                    this.context.formMode = FormMode.Reading;
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le groupe.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appelé lorsque les paramètres ont été mis à jour.
     * Il s'agit des modifications apportées à l'onglet Paramètres.
     * @param paramsValues : Paramètres à mettre à jour.
     */
    public paramsValuesUpdated(paramsValues: ParamsValues): void {
        this.spinner.show('main-spinner');
        if (paramsValues.id) {
            this.paramsValuesService.update(this.memoryService.getSelectedOrganization().id, paramsValues.id, paramsValues).subscribe(
                (x) => {
                    this.context.paramsContext.paramsValues = x;
                    this.displaySuccessNotification('Paramètres du groupe mis à jour');
                    this.context.formMode = FormMode.Reading;
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le paramétrage du groupe.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            this.paramsValuesService.createForGroup(this.context.groupContext.item.id, paramsValues).subscribe(
                (x) => {
                    this.context.paramsContext.paramsValues = x;
                    this.displaySuccessNotification('Paramètres du groupe créé');
                    this.context.formMode = FormMode.Reading;
                    this.spinner.hide('main-spinner');
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer le paramétrage du groupe.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appelé lorsque les accès aux bannettes ont été mis à jour.
     * Il s'agit des modifications apportées à l'onglet Accessibilité bannettes.
     * @param boxesAccessibility : Accès aux bannettes à mettre à jour.
     */
    public onAccessibilitiesUpdated(boxesAccessibility: BoxAccessibility[]): void {
        this.spinner.show('main-spinner');
        this.groupsService.updateBoxes(this.context.editedGroupId, boxesAccessibility).subscribe(
            (x) => {
                this.displaySuccessNotification('Bannettes du groupe mises à jour');
                this.context.formMode = FormMode.Reading;
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de mettre à jour les bannettes du groupe.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appelé lorsque les accès aux vues ont été mis à jour.
     * Il s'agit des modifications apportées à l'onglet Accessibilité vues.
     * @param viewsAccessibility : Accès aux vues à mettre à jour.
     */
    public onViewsAccessibilitiesUpdated(viewsAccessibility: ViewAccessibility[]): void {
        this.spinner.show('main-spinner');
        this.groupsService.updateViews(this.context.editedGroupId, viewsAccessibility).subscribe({
            next: (x) => {
                this.displaySuccessNotification('Vues du groupe mises à jour');
                this.context.formMode = FormMode.Reading;
                this.spinner.hide('main-spinner');
            },
            error: (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de mettre à jour les vues du groupe.' + error.message);
                this.spinner.hide('main-spinner');
            },
        });
    }

    /**
     * Appellé lorsque l'utilisateur confirme la réinitialisation d'un groupe.
     * @param group Group sélectionné.
     */
    public onGroupReseted(group: Group) {
        this.spinner.show('main-spinner');
        // Réinitialisation effective du groupe
        this.groupsService.reset(group.id).subscribe(
            (x) => {
                this.refreshListOrTree(group.id);
                this.displaySuccessNotification('Groupe réinitialisé');
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                console.error(error);
                this.notificationService.displayError('Erreur', `Impossible de réinitialiser le groupe.` + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton d'export.
     */
    public onGroupsExported() {
        this.groupsList.export('Groupes.xlsx');
    }

    /**
     * Appellée lorsque l'utilisateur clique sur le bouton d'aide.
     */
    public onGroupsHelped() {
        this.groupsList.displayHelpDialog('groups');
    }

    /**
     * Retourne une valeur indiquant si le formulaire est affiché (visible)
     * @returns True si le formulaire est visible.
     */
    public isFormDisplayed(): boolean {
        return DisplayMode.Form === (this.displayMode & DisplayMode.Form);
    }

    /**
     * Retourne une valeur indiquant si l'affichage des groupes sous la forme d'une arborescence est actif.
     * L'affichage des groupes peut être caché si dans le même temps le formulaire est visible.
     * @returns True si l'affichage des groupes sous la forme d'une arborescence est actif.
     */
    public isTreeDisplayed(): boolean {
        return DisplayMode.Tree === (this.displayMode & DisplayMode.Tree);
    }

    /**
     * Retourne une valeur indiquant si l'affichage des groupes sous la forme d'une liste est actif.
     * L'affichage des groupes peut être caché si dans le même temps le formulaire est visible.
     * @returns True si l'affichage des groupes sous la forme d'une liste est actif.
     */
    public isListDisplayed(): boolean {
        return DisplayMode.List === (this.displayMode & DisplayMode.List);
    }

    /**
     * Cache le formulaire.
     * La liste ou l'arboresence est alors visible.
     */
    public hideForm(): void {
        this.displayMode &= ~DisplayMode.Form;
    }

    /**
     * Définit le mode d'affichage de la page (Form / List / Tree).
     * Les modes List et Tree sont exclusifs. Par contre le mode Form peut être associés à l'un des 2 autres.
     * Exemple : si le mode est à Tree, l'appel de setDisplayMode(DisplayMode.Form) affichera le formulaire mais un click sur le header 'Groupes d'utilisateurs' affichera l'arborescence.
     * si le mode est à List, l'appel de setDisplayMode(DisplayMode.Form) affichera le formulaire mais un click sur le header 'Groupes d'utilisateurs' affichera la liste.
     * @param mode Mode d'affichage
     */
    public setDisplayMode(mode: DisplayMode) {
        if (mode == DisplayMode.Form) {
            this.displayMode |= mode;
        } else {
            this.displayMode = mode;
        }
    }

    /**
     * Charge les informations d'un groupe.
     * Il s'agit des données affichées dans l'onglet Infos du détail.
     * @param groupId : Id du groupe à charger.
     * @returns Les informations du groupe.
     */
    public loadGroup(groupId: string): Observable<Group> {
        return this.groupsService.get(groupId);
    }

    /**
     * Charge le paramétrage d'un groupe.
     * Il s'agit des données affichées dans l'onglet Paramètres du détail.
     * @param groupId : Id du groupe à charger.
     * @returns Les paramètres du groupe.
     */
    public loadParams(groupId: string): Observable<ParamsValues> {
        return this.paramsValuesService.getForGroup(groupId);
    }

    /**
     * Affiche Les données d'un groupe dans le formulaire.
     * @param group : Instance du groupe à afficher dans le formulaire.
     * @param paramsValues Paramétrage du groupe (Il s'agit des valeurs définies explicitement pour ce groupe sans héritage)
     * @param inheritedParamsValues Paramétrage hérités (Il s'agit des valeurs héritées des groupes parents et de l'oganisation).
     * @param formMode Mode d'affichage du formulaire.
     */
    private displayForm(group: IGroup, paramsValues: IParamsValues, inheritedParamsValues: IParamsValues, formMode: FormMode): void {
        this.createContext(group, paramsValues, inheritedParamsValues, formMode);
        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Recharge le formulaire pour le groupe spécifié
     * @param group Instance du groupe à afficher dans le formulaire.
     * @param formMode Mode d'affichage du formulaire.
     */
    private reloadForm(group: Group, formMode: FormMode) {
        this.spinner.show('main-spinner');
        const getGroup$ = this.groupsService.get(group.id);
        const params$ = this.loadParams(group.id);
        const inheritedParams$ = !!group.parentGroupId ? this.loadParams(group.parentGroupId) : of(null as ParamsValues);

        forkJoin([getGroup$, params$, inheritedParams$]).subscribe(
            (x) => {
                this.displayForm(x[0], x[1], x[2], formMode);
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le groupe." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Raffraichit le contenu de la liste ou de l'arborescence.
     * @param groupId Id du groupe dont l'affichage doit être raffraichi.
     */
    private refreshListOrTree(groupId: string) {
        if (this.isListDisplayed()) {
            this.groupsList.refresh();
        } else {
            this.groupsTree.refreshGroup(groupId);
        }
    }

    /**
     * Affiche une notification en cas de succès d'une action serveur.
     * @param notificationText: Texte de la notification.
     */
    private displaySuccessNotification(notificationText: string): void {
        this.notificationService.displaySuccess(null, notificationText);
    }

    /**
     * Crée un contexte utilisé pour l'affichage du formulaire.
     * @param group : Instance du groupe à afficher dans le formulaire.
     * @param paramsValues Paramétrage du groupe (Il s'agit des valeurs définies explicitement pour ce groupe sans héritage)
     * @param inheritedParamsValues Paramétrage hérités (Il s'agit des valeurs héritées des groupes parents et de l'oganisation).
     * @param formMode Mode d'affichage du formulaire.
     * @returns Le contexte utilisé pour l'affichage du formulaire.
     */
    private createContext(group: IGroup, paramsValues: IParamsValues, inheritedParamsValues: IParamsValues, formMode: FormMode): void {
        let context = new GroupDetailContext();
        context.groupContext.item = group;
        context.editedGroupId = group.id;
        context.paramsContext.paramsValues = paramsValues;
        context.paramsContext.groupParentParamsValues = inheritedParamsValues;
        context.formMode = formMode;

        this.context = context;
    }
}
